import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import { IReportingType } from "../../model/reporting/ReportingType";
import { IValueListLabel } from "../../model/valuelist/ValueListLabel";
import GetAttributeReferenceListInteractor from "../../interactor/valuelist/GetAttributeReferenceListInteractor";
import { IValueListItem } from "../../model/valuelist/ValueListItem";
import UpdateAttributeReferenceInteractor from "../../interactor/valuelist/UpdateAttributeReferenceInteractor";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import CreateAttributeReferenceInteractor from "../../interactor/valuelist/CreateAttribtueReferenceInteractor";
import DeleteAttributeReferenceListInteractor from "../../interactor/valuelist/DeleteAttributeReferenceListInteractor";
import GetAttributeCodeListInteractor from "../../interactor/valuelist/GetAttributeCodeListInteractor";
import DeleteAttributeReferenceInteractor from "../../interactor/valuelist/DeleteAttributeReferenceInteractor";

export interface TAttributeCodebookPresenter extends TLoadingAwarePresenter {
  attributeCodeList: IValueListLabel[];
  selectedAttributeCode?: string;
  referenceList?: IValueListItem[];
  attributeModalVisible: boolean;
}

export interface IAttributeCodebookPresenter
  extends TAttributeCodebookPresenter,
    TPresentable {
  loadAttributeCodeList(attributeCodeList: IValueListLabel[]): void;
  onChangeAttributeCode(attributeCode: string): void;
  updateAttributeReference(data: IValueListItem): void;
  createAttributeReference(data: IValueListItem): void;
  deleteAttributeReferenceList(): void;
  onChangeAttributeModalVisible(attributeModalVisible: boolean): void;
  createAttributeCodebook(data: IValueListItem): void;
  deleteAttributeReference(valueListItemId: number): void;
}

export interface IReportingTypePresentationModel extends IReportingType {
  key: string;
}

const defaultState: TAttributeCodebookPresenter = {
  attributeCodeList: [],
  selectedAttributeCode: undefined,
  referenceList: [],
  attributeModalVisible: false
};

const AttributeCodebookPresenter = withStore<
  IAttributeCodebookPresenter,
  TAttributeCodebookPresenter
>(({ application, store, loader, translate }): IAttributeCodebookPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TAttributeCodebookPresenter>();

  loader.start("attributeCodeListLoader");

  const loadAttributeCodeList = (attributeCodeList: IValueListLabel[]) => {
    _store.update({
      attributeCodeList
    });
    loader.stop("attributeCodeListLoader");
  };

  const getAttributeReferenceList = async (attributeCode: string) => {
    try {
      const referenceList =
        attributeCode &&
        (await application.container
          .resolve<GetAttributeReferenceListInteractor>(
            "getAttributeReferenceList"
          )
          .execute(attributeCode));
      _store.update({
        referenceList
      });
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const getAttributeCodeList = async () => {
    try {
      const attributeCodeList = await application.container
        .resolve<GetAttributeCodeListInteractor>("getAttributeCodeList")
        .execute();
      _store.update({
        attributeCodeList
      });
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const onChangeAttributeCode = async (attributeCode: string) => {
    loader.start("referenceListLoader");
    await getAttributeReferenceList(attributeCode);
    _store.update({
      selectedAttributeCode: attributeCode
    });
    loader.stop("referenceListLoader");
  };

  const updateAttributeReference = async (data: IValueListItem) => {
    try {
      const attributeCode =
        _store.getState<TAttributeCodebookPresenter>().selectedAttributeCode;
      await _application.container
        .resolve<UpdateAttributeReferenceInteractor>("updateAttributeReference")
        .execute(data);

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "attribute_codebook.messages.update_attribute_reference_success"
          )
        );
      attributeCode && (await getAttributeReferenceList(attributeCode));
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const createAttributeReference = async (data: IValueListItem) => {
    try {
      const attributeCode =
        _store.getState<TAttributeCodebookPresenter>().selectedAttributeCode;
      if (attributeCode) {
        let attributeReference = data;
        attributeReference.label = attributeCode;
        await _application.container
          .resolve<CreateAttributeReferenceInteractor>(
            "createAttributeReference"
          )
          .execute(data);

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "attribute_codebook.messages.create_attribute_reference_success"
            )
          );
        await getAttributeReferenceList(attributeCode);
      }
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const deleteAttributeReferenceList = async () => {
    try {
      const attributeCode =
        _store.getState<TAttributeCodebookPresenter>().selectedAttributeCode;
      if (attributeCode) {
        await _application.container
          .resolve<DeleteAttributeReferenceListInteractor>(
            "deleteAttributeReferenceList"
          )
          .execute(attributeCode);

        _application.container
          .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
          .execute(
            translate(
              "attribute_codebook.messages.delete_attribute_reference_success"
            )
          );
        await getAttributeCodeList();
        _store.update({
          selectedAttributeCode: undefined,
          referenceList: []
        });
        await getAttributeCodeList();
      }
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const onChangeAttributeModalVisible = (attributeModalVisible: boolean) => {
    _store.update({
      attributeModalVisible
    });
  };

  const createAttributeCodebook = async (data: IValueListItem) => {
    try {
      await _application.container
        .resolve<CreateAttributeReferenceInteractor>("createAttributeReference")
        .execute(data);

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "attribute_codebook.messages.create_attribute_reference_success"
          )
        );
      await getAttributeCodeList();
      _store.update({
        selectedAttributeCode: data.label
      });
      await getAttributeReferenceList(data.label);
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const deleteAttributeReference = async (valueListItemId: number) => {
    try {
      await _application.container
        .resolve<DeleteAttributeReferenceInteractor>("deleteAttributeReference")
        .execute(valueListItemId);

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate("attribute_codebook.messages.delete_attribute_success")
        );
      const selectedAttributeCode =
        _store.getState<TAttributeCodebookPresenter>().selectedAttributeCode;
      selectedAttributeCode &&
        (await getAttributeReferenceList(selectedAttributeCode));
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    loadAttributeCodeList,
    onChangeAttributeCode,
    updateAttributeReference,
    createAttributeReference,
    deleteAttributeReferenceList,
    onChangeAttributeModalVisible,
    createAttributeCodebook,
    deleteAttributeReference
  };
}, defaultState);

export default AttributeCodebookPresenter;
